import { useRef, useState } from 'react';

import {
  ListingType,
  PropertyType,
  RentalManagerUserPropertyLocationRestrictionsWebApiHandler_restrictions,
} from '@zg-rentals/ts-rental-manager-api-client';

import { logError } from '@zg-rentals/log-error';

import getLocationRestrictionErrorMessage from '../utils/getLocationRestrictionErrorMessage';
import PropertyConstants from '../constants/PropertyConstants';

import type { RentalManagerAuthCheckUserInfoWebApiDto } from '@zg-rentals/ts-rental-manager-api-client';

const {
  PROPERTY_TYPES: { COMMUNITY, CONDO, MULTIPLEX, TOWNHOUSE, HOUSE },
} = PropertyConstants;

const PROPERTY_TYPE_TO_ENUM: Record<string, PropertyType> = {
  [COMMUNITY]: PropertyType.LARGE,
  [MULTIPLEX]: PropertyType.CONDO,
  [CONDO]: PropertyType.CONDO,
  [TOWNHOUSE]: PropertyType.TOWNHOUSE,
  [HOUSE]: PropertyType.HOUSE,
};

type User = Pick<
  RentalManagerAuthCheckUserInfoWebApiDto,
  'isSingleFamilyEnterpriseCustomer' | 'isMultifamilyCustomer' | 'isPostingPathAdmin'
>;

type Params = {
  user?: User;
  setPropertyAlreadyPostedWarningMessage: (msg: string | Array<JSX.Element | string>) => void;
};

type CheckParams = {
  address: string;
  unit?: string;
  propertyType?: string;
  listingType?: ListingType;
  // Skips message building, which clears the posted warning and logs a LocationRestrictionError.
  skipMessage?: boolean;
};

export const usePropertyLocationCheck = ({ user, setPropertyAlreadyPostedWarningMessage }: Params) => {
  const [isCheckingRestrictions, setIsCheckingRestrictions] = useState(false);
  // Counted so concurrent checks do not clear it when the first one lands.
  const pendingChecksRef = useRef(0);

  const checkLocationRestriction = async ({
    address,
    unit,
    propertyType,
    listingType = ListingType.RENTAL,
    skipMessage = false,
  }: CheckParams) => {
    if (!address) return null;

    pendingChecksRef.current += 1;
    setIsCheckingRestrictions(true);
    try {
      const result = await RentalManagerUserPropertyLocationRestrictionsWebApiHandler_restrictions({
        fullAddress: address,
        unit,
        listingType,
        propertyType: propertyType ? PROPERTY_TYPE_TO_ENUM[propertyType] : undefined,
      });

      const normalized = { ...result, paidMultifamilyLocation: result.isPaidMultifamily };
      const isMultifamily = propertyType === COMMUNITY;

      const errorMessage =
        user && !skipMessage
          ? getLocationRestrictionErrorMessage(
              normalized,
              isMultifamily,
              user.isMultifamilyCustomer,
              false,
              user.isSingleFamilyEnterpriseCustomer,
              user.isPostingPathAdmin,
              setPropertyAlreadyPostedWarningMessage,
            )
          : null;

      return { ...normalized, errorMessage };
    } catch (error) {
      logError({ error, context: 'usePropertyLocationCheck' });
      return null;
    } finally {
      pendingChecksRef.current -= 1;
      if (pendingChecksRef.current === 0) {
        setIsCheckingRestrictions(false);
      }
    }
  };

  return { isCheckingRestrictions, checkLocationRestriction };
};
